import { Box, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { Link } from '#features/routing';
import { Button } from '#features/ui';
import { statsProviderForGame } from '#features/ui/components/ConnectAccountNudge/gameStatsProviders';
import {
  STATS_EDGE,
  STATS_LABEL,
  STATS_PANEL_SURFACE,
  STATS_RADIUS,
  STATS_RADIUS_INNER,
  STATS_SCRIM,
  STATS_TILES,
  statsSurface,
} from '#features/ui/components/gameStatsSurface';

type GameStatsProps = {
  gameName: string;
  gameSlug: string;
  accentColor: string;
};

/**
 * Фоны игровых карточек, которые уже лежат в `public/game-bg`. У Marathon фона нет —
 * карточка остаётся на градиенте, это штатный fallback `statsSurface`.
 */
const BG_BY_SLUG: Record<string, string> = {
  cs2: '/game-bg/cs2.jpg',
  'destiny-2': '/game-bg/destiny-2.jpg',
  'dota-2': '/game-bg/dota-2.jpg',
  dota2: '/game-bg/dota-2.jpg',
  fortnite: '/game-bg/fortnite.jpg',
  'league-of-legends': '/game-bg/league-of-legends.jpg',
};

/**
 * Игры, у которых на лендинге есть блок статистики. Marathon в списке нет намеренно:
 * статистики по ней мы не показываем — привязка Steam даёт только наигранные часы.
 *
 * Отдельный экспорт нужен лендингу: без него секция-обёртка со своими вертикальными
 * отступами рисовалась бы и там, где компонент возвращает null, — дырой в 100+ px.
 */
const STATS_SLUGS = new Set([
  'league-of-legends',
  'dota-2',
  'dota2',
  'cs2',
  'counter-strike-2',
  'fortnite',
  'destiny-2',
]);

export const hasGameStats = (slug: string): boolean => STATS_SLUGS.has(slug);

/** Строка «подпись: значение» под сеткой чисел — то, чем в блоке заканчивается карточка. */
type SampleChip = { label: string; value: string };

/**
 * Образец карточки одной игры. Состав повторяет анатомию настоящего блока на заявке:
 * контекстный чип в шапке, герой-число, сетка показателей, характерные детали внизу.
 */
type Sample = {
  context: string;
  hero: { value: string; label: string; note?: string };
  cells: { key: string; value: string; label: string }[];
  chips: SampleChip[];
};

/**
 * Мини-блок «покажи свою статистику»: живёт только на страницах игр, по которым статистика
 * действительно собирается (`STATS_PROVIDER_BY_SLUG`). На остальных лендингах возвращает
 * null — обещание без данных хуже, чем его отсутствие.
 *
 * Карточка собрана из того же материала, что настоящие блоки статистики на заявке
 * (`gameStatsSurface`), и показывает те же поля, что реально приезжают от провайдера:
 * человек, дошедший до заявки, должен увидеть ровно то, что ему обещали здесь.
 */
export const GameStats = ({ gameName, gameSlug, accentColor }: GameStatsProps) => {
  const { t } = useLingui();

  // Провайдер — гарантия, что статистика по игре вообще собирается; список выше — что она
  // собирается в том виде, который есть смысл показывать на лендинге.
  if (!statsProviderForGame({ slug: gameSlug }) || !hasGameStats(gameSlug)) {
    return null;
  }

  /**
   * Числа-образец. Правдоподобный, но выдуманный игрок — и подписан в карточке как «Example».
   *
   * Не `Math.random()`: карточка рисуется на SSR, случайное значение разъедется с клиентским
   * рендером и даст hydration mismatch, а прыгающая на каждом заходе статистика читается как
   * сломанные данные, а не как пример.
   *
   * Состав полей у каждой игры сверен с её блоком (`LolRankBlock`, `DotaStatsBlock`,
   * `Cs2StatsBlock`, `FortniteStatsBlock`, `DestinyStatsBlock`, `GamePlaytimeBlock`):
   * лендинг не должен показывать показатель, которого после привязки не появится.
   */
  const SAMPLES: Record<string, Sample> = {
    'league-of-legends': {
      context: t`Solo/Duo · ranked`,
      hero: { value: 'Emerald II', label: t`Rank`, note: `68 ${t`pts`}` },
      cells: [
        { key: 'wr', value: '56%', label: t`Win rate` },
        { key: 'kda', value: '3.4', label: t`KDA` },
        { key: 'cs', value: '7.2', label: t`CS/min` },
      ],
      chips: [
        { label: t`Champion mastery`, value: "Jinx · Kai'Sa" },
        { label: t`Recent form`, value: '7W · 3L' },
      ],
    },
    'dota-2': {
      context: t`Averages · match history`,
      hero: { value: '1 284', label: t`Matches`, note: `53% ${t`WR`}` },
      cells: [
        { key: 'gpm', value: '486 / 552', label: t`GPM / XPM` },
        { key: 'lhd', value: '212 / 8', label: t`LH / D` },
        { key: 'avg', value: `38 ${t`min`}`, label: t`Avg match` },
      ],
      chips: [
        { label: t`Top heroes`, value: 'Juggernaut · Lion' },
        { label: t`Last 2 weeks`, value: `14 ${t`hrs`}` },
      ],
    },
    cs2: {
      context: t`Lifetime · all modes`,
      hero: { value: `1 240 ${t`hrs`}`, label: t`Time in game`, note: '1.12 K/D' },
      cells: [
        { key: 'wr', value: '51%', label: t`Win rate` },
        { key: 'adr', value: '78.4', label: t`ADR` },
        { key: 'hs', value: '47%', label: t`Headshots` },
      ],
      chips: [
        { label: t`Signature weapon`, value: 'AK-47' },
        { label: t`Top map`, value: 'Mirage' },
      ],
    },
    fortnite: {
      context: t`Lifetime · all modes`,
      hero: { value: '212', label: t`Victory royales`, note: '1.84 K/D' },
      cells: [
        { key: 'matches', value: '3 940', label: t`Matches` },
        { key: 'wr', value: '5.4%', label: t`Win rate` },
        { key: 'elims', value: '3.1', label: t`Elims / match` },
      ],
      chips: [
        { label: t`Modes`, value: 'Solo · Duos' },
        { label: t`Hrs played`, value: `640 ${t`hrs`}` },
      ],
    },
    'destiny-2': {
      context: t`Lifetime · all characters`,
      hero: { value: '2010', label: t`Power`, note: 'Warlock' },
      cells: [
        { key: 'kd', value: '1.42', label: t`Crucible K/D` },
        { key: 'wr', value: '52%', label: t`Crucible win rate` },
        { key: 'pve', value: '1 870', label: t`PvE activities` },
      ],
      chips: [
        { label: t`Guardians`, value: 'Warlock · Titan' },
        { label: t`Time in game`, value: `640 ${t`hrs`}` },
      ],
    },
    // Marathon здесь нет намеренно: статистики по игре мы не показываем — привязка Steam
    // даёт только наигранные часы из библиотеки, и звать ради них на лендинге не за чем.
  };
  SAMPLES.dota2 = SAMPLES['dota-2'];
  SAMPLES['counter-strike-2'] = SAMPLES.cs2;

  const sample = SAMPLES[gameSlug];

  if (!sample) {
    return null;
  }

  const fallbackSurface = 'linear-gradient(135deg, #0a0a0b 0%, #17181a 55%, #101012 100%)';
  const bgImage = BG_BY_SLUG[gameSlug];
  // Без картинки собираем фон руками: `url('')` в shorthand роняет весь background.
  const surface = bgImage
    ? statsSurface(bgImage, fallbackSurface)
    : `${STATS_SCRIM}, ${fallbackSurface}`;

  return (
    <Flex
      // На мобилке карточка идёт ПОД текстом: сначала обещание, потом доказательство -
      // сверху экрана должно стоять то, что объясняет, зачем на эти числа смотреть.
      // Порядок в разметке (текст, карточка) заодно ставит заголовок секции первым, а
      // `row-reverse` возвращает карточке левую колонку на десктопе.
      direction={{ base: 'column', md: 'row-reverse' }}
      gap={{ base: 8, md: 12 }}
      alignItems="center"
      justifyContent="center"
      maxW="920px"
      mx="auto"
      w="100%"
    >
      <Stack
        gap={5}
        flex="1"
        alignItems={{ base: 'center', md: 'flex-start' }}
        textAlign={{ base: 'center', md: 'left' }}
      >
        <Text
          fontSize="xs"
          fontWeight="700"
          color="purple.300"
          textTransform="uppercase"
          letterSpacing="0.15em"
        >
          {t`Proof, not promises`}
        </Text>
        <Heading
          as="h2"
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="900"
          letterSpacing="-0.02em"
          lineHeight="1.05"
          textTransform="uppercase"
          css={{ textWrap: 'balance' }}
        >
          {t`Your real ${gameName} stats on every post`}
        </Heading>
        <Text fontSize={{ base: 'md', md: 'lg' }} color="fg.muted" lineHeight="1.7" maxW="520px">
          {/* Про ранг здесь говорить нельзя: у CS2, Fortnite и Marathon в карточке ранга нет,
              и обещание разошлось бы с тем, что человек увидит на своей заявке. */}
          {t`Link your game account once and your ${gameName} numbers ride along with every LFG post you publish. No screenshots, no bragging - teammates see who they are queuing with before they accept.`}
        </Text>
        <Button size="lg" minW="220px" style={{ boxShadow: `0 0 26px ${accentColor}55` }} asChild>
          <Link to="/{-$locale}/sign-up">{t`Create a free account`}</Link>
        </Button>
        <Text fontSize="xs" color="fg.subtle" maxW="460px">
          {t`Numbers in the card are an example - yours show up as soon as you link your account.`}
        </Text>
      </Stack>

      {/* Карточка-образец: тот же материал, что у настоящего блока на заявке. */}
      <Box
        w={{ base: '100%', md: '400px' }}
        flexShrink={0}
        p={3.5}
        borderRadius={STATS_RADIUS}
        borderWidth="1px"
        overflow="hidden"
        style={{
          background: surface,
          borderColor: STATS_EDGE,
          boxShadow: `0 20px 50px -20px rgba(0,0,0,0.7), 0 0 30px -14px ${accentColor}66`,
        }}
      >
        {/* Ни иконки, ни имени провайдера: набор игровых платформ на регистрации может
            измениться (они не отдают email), и лендинг не должен обещать конкретную из них. */}
        <Flex gap={2.5} alignItems="center" flexWrap="wrap" mb={3}>
          <Text fontSize="sm" fontWeight={700} color="fg.emphasized" whiteSpace="nowrap">
            {gameName}
          </Text>
          {/* Числа выдуманные, и это должно быть написано на самой карточке, а не только
              в подписи под ней: иначе образец читается как чей-то реальный профиль. */}
          <Text
            as="span"
            px={1.5}
            py={0.5}
            borderRadius="full"
            bgColor="whiteAlpha.100"
            fontSize="2xs"
            fontWeight={700}
            letterSpacing="wide"
            textTransform="uppercase"
            color="fg.secondary"
            whiteSpace="nowrap"
          >
            {t`Example`}
          </Text>
        </Flex>

        <Text
          fontSize="2xs"
          fontWeight={800}
          letterSpacing="0.12em"
          textTransform="uppercase"
          color={STATS_LABEL}
          mb={2}
        >
          {sample.context}
        </Text>

        <HStack
          gap={3}
          p={3}
          borderRadius={STATS_RADIUS_INNER}
          alignItems="baseline"
          flexWrap="wrap"
          {...STATS_PANEL_SURFACE}
        >
          <Text
            fontSize={{ base: '2xl', sm: '3xl' }}
            fontWeight={900}
            color="white"
            fontVariantNumeric="tabular-nums"
            lineHeight={1}
          >
            {sample.hero.value}
          </Text>
          <Text
            fontSize="2xs"
            fontWeight={800}
            letterSpacing="0.12em"
            textTransform="uppercase"
            color={STATS_LABEL}
          >
            {sample.hero.label}
          </Text>
          {sample.hero.note && (
            <Text
              fontSize="sm"
              fontWeight={700}
              color="fg"
              fontVariantNumeric="tabular-nums"
              ml="auto"
            >
              {sample.hero.note}
            </Text>
          )}
        </HStack>

        <Box
          mt={3}
          display="grid"
          gridTemplateColumns={`repeat(${sample.cells.length}, 1fr)`}
          {...STATS_TILES}
        >
          {sample.cells.map((cell) => (
            <Stack key={cell.key} gap={1} minW={0}>
              <Text
                fontSize="lg"
                fontWeight={700}
                lineHeight="1"
                color={accentColor}
                fontVariantNumeric="tabular-nums"
                truncate
              >
                {cell.value}
              </Text>
              <Text
                fontSize="2xs"
                fontWeight={700}
                letterSpacing="0.06em"
                textTransform="uppercase"
                color={STATS_LABEL}
                truncate
              >
                {cell.label}
              </Text>
            </Stack>
          ))}
        </Box>

        {sample.chips.length > 0 && (
          <Stack gap={2} mt={3}>
            {sample.chips.map((chip) => (
              <HStack key={chip.label} justifyContent="space-between" gap={3}>
                <Text
                  fontSize="2xs"
                  fontWeight={700}
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                  color={STATS_LABEL}
                  truncate
                >
                  {chip.label}
                </Text>
                <Text fontSize="xs" fontWeight={700} color="fg" whiteSpace="nowrap">
                  {chip.value}
                </Text>
              </HStack>
            ))}
          </Stack>
        )}
      </Box>
    </Flex>
  );
};
